import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const OrgContext = createContext(null);
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = 'active_org_id';

// Module-level request interceptor: attach the active sub-account scope to every
// API call. Validated & ownership-checked server-side, so this can't escalate.
axios.interceptors.request.use((config) => {
  try {
    const orgId = localStorage.getItem(STORAGE_KEY);
    if (orgId) config.headers['X-Org-Id'] = orgId;
    else if (config.headers) delete config.headers['X-Org-Id'];
  } catch { /* ignore */ }
  return config;
});

export const OrgProvider = ({ children }) => {
  const { token } = useAuth();
  const [hq, setHq] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [activeOrgId, setActiveOrgId] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) || null; } catch { return null; }
  });
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/organisations`);
      setHq(res.data.hq);
      setOrgs(res.data.organisations || []);
      // If the stored active org no longer exists, fall back to HQ.
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored && !(res.data.organisations || []).some((o) => o.id === stored)) {
        localStorage.removeItem(STORAGE_KEY);
        setActiveOrgId(null);
      }
    } catch { /* ignore */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (token) refresh(); else setLoading(false); }, [token, refresh]);

  const switchOrg = (orgId) => {
    try {
      if (orgId) localStorage.setItem(STORAGE_KEY, orgId);
      else localStorage.removeItem(STORAGE_KEY);
    } catch { /* ignore */ }
    // Hard reload so every page refetches under the new scope.
    window.location.assign('/');
  };

  const activeOrg = activeOrgId ? orgs.find((o) => o.id === activeOrgId) : null;

  return (
    <OrgContext.Provider value={{ hq, orgs, activeOrgId, activeOrg, loading, refresh, switchOrg }}>
      {children}
    </OrgContext.Provider>
  );
};

export const useOrg = () => {
  const ctx = useContext(OrgContext);
  if (!ctx) throw new Error('useOrg must be used within an OrgProvider');
  return ctx;
};
